import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Permission, Role, UpdateProfileInput, UserDTO } from '@astral/shared';
import { getInitData, isInsideTelegram } from '@astral/telegram/webapp';
import { api, ApiClientError, setToken } from '../lib/api.js';
import { writeCachedPrefs } from '../lib/prefs.js';

export type AuthStatus =
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'not-in-telegram'
  | 'error';

interface AuthContextValue {
  status: AuthStatus;
  user: UserDTO | null;
  permissions: Permission[];
  error: string | null;
  /** (Re)attempt sign-in. */
  login: () => Promise<void>;
  /** Optimistically update preferences and persist them. */
  updatePreferences: (input: UpdateProfileInput) => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<UserDTO | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const login = useCallback(async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    setError(null);

    try {
      const initData = getInitData();

      if (initData) {
        const auth = await api.loginWithTelegram(initData);
        setToken(auth.token);
        setUser(auth.user);
        setPermissions(auth.permissions);
        setStatus('authenticated');
      } else if (import.meta.env.DEV) {
        // Browser development outside Telegram.
        const auth = await api.loginDev();
        setToken(auth.token);
        setUser(auth.user);
        setPermissions(auth.permissions);
        setStatus('authenticated');
      } else if (!isInsideTelegram()) {
        setStatus('not-in-telegram');
      } else {
        setStatus('error');
        setError('Missing Telegram session data.');
      }
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Sign-in failed';
      setError(message);
      setStatus('error');
    } finally {
      inFlight.current = false;
    }
  }, []);

  // Attempt sign-in once on mount.
  useEffect(() => {
    void login();
  }, [login]);

  // Mirror display preferences into localStorage so the correct language/theme
  // paint instantly on the next reload, before login resolves.
  useEffect(() => {
    if (user) {
      writeCachedPrefs({
        language: user.language,
        themeId: user.themeId,
        themeMode: user.themeMode,
      });
    }
  }, [user]);

  const updatePreferences = useCallback(
    async (input: UpdateProfileInput): Promise<void> => {
      setUser((prev) => (prev ? { ...prev, ...input } : prev));
      try {
        const result = await api.updateMe(input);
        setUser(result.user);
        setPermissions(result.permissions);
      } catch (err) {
        // Reload authoritative state on failure.
        try {
          const fresh = await api.me();
          setUser(fresh.user);
          setPermissions(fresh.permissions);
        } catch {
          /* ignore secondary failure */
        }
        throw err;
      }
    },
    [],
  );

  const hasPermission = useCallback(
    (permission: Permission): boolean => permissions.includes(permission),
    [permissions],
  );

  const value: AuthContextValue = {
    status,
    user,
    permissions,
    error,
    login,
    updatePreferences,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** Convenience hook that asserts an authenticated user is present. */
export function useCurrentUser(): UserDTO {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser called without an authenticated user');
  return user;
}

/**
 * The signed-in user's ROLE, for product gating that reads role and plan as two
 * independent dimensions (see `canCustomizePackBranding`).
 *
 * Falls back to the LEAST privileged role while no session is loaded, so a
 * role-gated affordance is never shown before the session says so. Deliberately
 * non-throwing, unlike {@link useCurrentUser}: this answers "what may this
 * account customise", not "who is signed in", and it is display only — every
 * authorization decision is still the backend's.
 */
export function useCurrentRole(): Role {
  const ctx = useContext(AuthContext);
  return ctx?.user?.role ?? 'USER';
}
